// Team plan state, shared by the sidebar, Manage Subscription, Team, Overview
// and Team usage. Mirrors the backend rule (utils/orgStore.js isOrgInGoodStanding).

import { useEffect, useState } from "react";

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
export const PAST_DUE_GRACE_DAYS = 3;
const TRIAL_PAYMENT_PROCESSING_HOURS = 24;

export const PAUSED_STATUSES = ["TrialExpired", "Suspended", "Canceled"];

export const isPausedStatus = (status) => PAUSED_STATUSES.includes(status);

/**
 * True while the team gives its members Pro. Takes either the /org/me
 * membership (orgStatus, trialEndsAt…) or an org from /overview (status…).
 */
export const isTeamActive = (team) => {
  if (!team) return false;
  const status = team.orgStatus ?? team.status;
  const now = Date.now();
  if (status === "Active") return true;
  if (status === "PastDue") {
    return !!team.pastDueSince && now - new Date(team.pastDueSince).getTime() < PAST_DUE_GRACE_DAYS * DAY_MS;
  }
  if (status === "Trialing" && team.trialEndsAt) {
    const ends = new Date(team.trialEndsAt).getTime();
    if (now < ends) return true;
    return !!team.billingAuthorizedAt && now < ends + TRIAL_PAYMENT_PROCESSING_HOURS * HOUR_MS;
  }
  return false;
};

// setTimeout's limit; later deadlines are picked up on a later render.
const MAX_TIMER_MS = 2 ** 31 - 1;

/**
 * Re-renders the component when the soonest of these moments passes (trial
 * end, reactivation deadline…), so the plan state shown flips on time without
 * waiting for a refetch. `everyMs` also ticks while one is near, for
 * countdowns ("4 min left").
 */
export const useRerenderAt = (times, { everyMs = 0, within = HOUR_MS } = {}) => {
  const [tick, setTick] = useState(0);
  const key = (times || []).map((t) => (t ? new Date(t).getTime() || 0 : 0)).join(",");

  useEffect(() => {
    const now = Date.now();
    const next = key
      .split(",")
      .map(Number)
      .filter((t) => t > now)
      .sort((a, b) => a - b)[0];
    if (!next) return undefined;
    // A second after the moment, so every Date.now() check sees it as passed.
    let delay = next - now + 1000;
    if (everyMs && next - now <= within) delay = Math.min(delay, everyMs);
    if (delay > MAX_TIMER_MS) return undefined;
    const id = setTimeout(() => setTick((n) => n + 1), delay);
    return () => clearTimeout(id);
  }, [key, tick, everyMs, within]);
};

/** "4 min left", "3 h left", "12 days left": time until a deadline. */
export const timeLeftText = (until) => {
  const ms = until ? new Date(until).getTime() - Date.now() : NaN;
  if (!Number.isFinite(ms)) return null;
  if (ms <= 0) return "Ending now";
  if (ms < HOUR_MS) return `${Math.max(1, Math.ceil(ms / MINUTE_MS))} min left`;
  if (ms < DAY_MS) return `${Math.ceil(ms / HOUR_MS)} h left`;
  const days = Math.ceil(ms / DAY_MS);
  return `${days} ${days === 1 ? "day" : "days"} left`;
};

export const isOnTrial = (team) => (team?.orgStatus ?? team?.status) === "Trialing";

/** Whole days left in the trial, rounded up (0 once it has ended). */
export const trialDaysLeft = (trialEndsAt) => {
  if (!trialEndsAt) return 0;
  return Math.max(0, Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / DAY_MS));
};

/** Share of the trial already used, 0–100, for the progress bar. */
export const trialProgress = (trialStartedAt, trialEndsAt) => {
  if (!trialStartedAt || !trialEndsAt) return 0;
  const start = new Date(trialStartedAt).getTime();
  const end = new Date(trialEndsAt).getTime();
  if (end <= start) return 100;
  return Math.min(100, Math.max(0, ((Date.now() - start) / (end - start)) * 100));
};

/** "3 Nov 2026, 10:15 UTC": the exact moment, as billing screens show it. */
export const formatUtcDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC" });
  return `${day}, ${time} UTC`;
};

/** "Today" / "Yesterday" / "29 Oct" for last-active dates. */
export const formatLastActive = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(new Date()) - startOfDay(d)) / DAY_MS);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

export const formatDayMonth = (value) => {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "";
};

export const centsToUsd = (cents, { trimZeros = false } = {}) => {
  const n = Number(cents || 0) / 100;
  const fixed = n.toFixed(2);
  return trimZeros && fixed.endsWith(".00") ? fixed.slice(0, -3) : fixed;
};
